import { createContext, useContext, useEffect, useState, useRef } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import { CONSTANTS } from '../../../constants/constants';
import NRSBOMDescription from './NRSBOMDescription/NRSBOMDescription';
import NRKnownExploits from '../../../components/NRVulnerabilitiesTable/NRVulnerabilityDetails/NRKnownExploits/NRKnownExploits';
import NRSBOMVulnExploits from './NRSBOMVulnExploits/NRSBOMVulnExploits';
import useNRQuery from '../../../graphql/useNRQuery';
import { DEPENDENCY_KNOWN_EXPLOITS, PACKAGE_DEPENDENCIES } from '../../../graphql/queries/dependency';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import NRAssociatedFiles from '../../../components/NRAssociatedFiles/NRAssociatedFiles';
import NRDependencies from '../../../components/NRDependencies/NRDependencies';
import useNRLazyQuery from '../../../graphql/useNRLazyQuery';
import { FiltersContext } from '../../NRReportTabs/NRReportFilterContextWrapper';
import { encodeParam } from '../../../services/params.utils';
import { useTheme } from '@mui/material/styles';
import { removeTabAndSubTabFromFilePath } from '../../../services/urlInterceptor.utils';
import EllipsisTypography from '../../../components/EllipsisTypography/EllipsisTypography';
const { ALERT } = CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER;
const { DESCRIPTION, VULNERABILITIES, REFERENCE_LINKS, ASSOCIATED_FILES, DEPENDENCIES, DEPENDENTS } =
  CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER.TABS;
const { EQUAL } = CONSTANTS.FILTER_OPERATIONS;
const { LOADING } = CONSTANTS.TABLE.MISCONFIGURATION_TABLE.MISCONFIGURATION_DETAIL_DRAWER;

export const DependencyDrawerTabsContext = createContext({
  currentTab: null,
  setCurrentTab: () => {},
  tabOrder: null,
  scrollSpyTabsValue: null,
  setScrollSpyTabsValue: () => {}
});

function NRSBOMDrawer({ row, scrollTo }) {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(DESCRIPTION);
  const { componentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabOrder] = useState([DESCRIPTION, VULNERABILITIES, REFERENCE_LINKS, ASSOCIATED_FILES, DEPENDENCIES, DEPENDENTS]);
  const [scrollSpyTabsValue, setScrollSpyTabsValue] = useState([true, false, false, false, false, false]);
  const [knownExploitsCount, setKnownExploitsCount] = useState(null);
  const [scrollableElement, setScrollableElement] = useState(null);
  const [scrollThreshold, setScrollThreshold] = useState([0, 0, 0, 0, 0, 0, 0]);
  const { setDrawerTitle, drawerTitle, setOpenDrawer, openDrawer } = useContext(FiltersContext);
  const [tabClicked, setTabClicked] = useState(false);
  const isScrollingProgrammatically = useRef(false);

  const [packageDependencies, setPackageDependencies] = useState(null);
  const [packageDependents, setPackageDependents] = useState(null);
  const [associatedFiles, setAssociatedFiles] = useState(null);
  const [knownExploits, knownExploitsLoading] = useNRQuery({
    query: DEPENDENCY_KNOWN_EXPLOITS,
    options: {
      variables: { args: { composedAssetId: componentId, identificationIds: row?.dependency?.identificationIds } },
      context: { version: 'v3' }
    },
    qlObjKey: 'dependencyKnownExploits',
    transformData: data => {
      return { exploit: { references: { ...data } } };
    }
  });

  function handleDependencyClick(dependency) {
    const { name, version } = dependency?.component;
    const filtersToSend = [
      ...(!!name
        ? [
            {
              fieldName: 'NAME',
              value: name,
              operation: EQUAL.toUpperCase()
            }
          ]
        : []),
      ...(!!version?.isConcrete
        ? [
            {
              fieldName: 'VERSION',
              value: version.id,
              operation: EQUAL.toUpperCase()
            }
          ]
        : [])
    ];

    const extraParams = new URLSearchParams();
    extraParams.append('componentColumnFilter', encodeParam(filtersToSend));
    extraParams.append('componentGroupedBy', '');
    navigate(`${removeTabAndSubTabFromFilePath(location)}/${CONSTANTS.REPORT.TABS.SBOM}?${extraParams.toString()}`);
    setOpenDrawer(false);
  }

  const [packageDependenciesByIdResponse, packageDependenciesByIdLoading, packageDependenciesByIdCalled, getPackageDependenciesById] = useNRLazyQuery(
    {
      query: PACKAGE_DEPENDENCIES,
      options: { context: { version: 'v3' }, fetchPolicy: 'no-cache' },
      qlObjKey: 'packageDependenciesById',
      transformData: data => {
        const { dependencies, associatedFiles } = data;
        setPackageDependencies([
          ...dependencies?.direct?.map(directItem => ({ ...directItem, direct: true })),
          ...dependencies?.indirect?.map(indirectItem => ({ ...indirectItem, direct: false }))
        ]);
        setPackageDependents(dependencies.dependents);
        setAssociatedFiles(associatedFiles);
        return data;
      }
    }
  );

  useEffect(() => {
    !!row?.dependency?.identificationIds &&
      getPackageDependenciesById({
        variables: { args: { composedAssetId: componentId, identificationIds: row?.dependency?.identificationIds } },
        context: { version: 'v3' }
      });
  }, [row?.dependency?.identificationIds]);

  useEffect(() => {
    if (scrollableElement && openDrawer && scrollTo && !packageDependenciesByIdLoading && !knownExploitsLoading) {
      isScrollingProgrammatically.current = true;
      setCurrentTab(scrollTo);
      setTabClicked(true);

      // Delay the scroll to ensure tab is selected first
      setTimeout(() => {
        handleChange(null, scrollTo);

        // Reset the programmatic scrolling flag after scroll completes
        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 800);
      }, 100);
    }
  }, [openDrawer, scrollableElement, packageDependenciesByIdLoading, knownExploitsLoading]);

  const scrollToPosition = position => {
    scrollableElement.scrollTo({
      top: position,
      behavior: 'smooth'
    });
  };

  const descriptionScrollIntoView = () => scrollToPosition(getScrollIndex(0) + 1);
  const vulnerabilitiesScrollIntoView = () => scrollToPosition(getScrollIndex(1) + 1);
  const referencesScrollIntoView = () => scrollToPosition(getScrollIndex(2) + 1);
  const filesScrollIntoView = () => scrollToPosition(getScrollIndex(3) + 1);
  const dependenciesScrollIntoView = () => scrollToPosition(getScrollIndex(4) + 1);
  const dependentsScrollIntoView = () => scrollToPosition(getScrollIndex(5) + 1);

  const handleChange = (event, newValue) => {
    isScrollingProgrammatically.current = true;
    switch (newValue) {
      case DESCRIPTION:
        descriptionScrollIntoView();
        break;
      case VULNERABILITIES:
        vulnerabilitiesScrollIntoView();
        break;
      case REFERENCE_LINKS:
        referencesScrollIntoView();
        break;
      case ASSOCIATED_FILES:
        filesScrollIntoView();
        break;
      case DEPENDENCIES:
        dependenciesScrollIntoView();
        break;
      case DEPENDENTS:
        dependentsScrollIntoView();
        break;
      default:
        break;
    }
    setTabClicked(true);
    setCurrentTab(newValue);

    // Increase timeout to account for smooth scroll animation
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 800);
  };

  const NRSBOMDrawerTitle = () => (
    <EllipsisTypography
      variant="h3"
      component="div"
      mt={0.5}
      children={`${row?.dependency?.name} ${!!row?.dependency?.version?.id ? row?.dependency?.version?.id : ''}`}
    />
  );

  useEffect(() => {
    if (!!row && row?.analytic) {
      const totalExploits = Object.keys(row.analytic.exploit).reduce((previousValue, currentValue) => {
        if (row?.analytic.exploit[currentValue] && !currentValue.startsWith('_')) {
          return previousValue + row?.analytic.exploit[currentValue];
        } else {
          return previousValue;
        }
      }, 0);
      setKnownExploitsCount(totalExploits);
    }
    if (!!row) {
      drawerTitle.at(-1) === LOADING && drawerTitle.pop();
      setDrawerTitle([...drawerTitle, <NRSBOMDrawerTitle />]);
    }
  }, [row]);

  useEffect(() => {
    const scrollableElem = document.querySelector('#dependency-details-main-container');
    if (scrollableElem) setScrollableElement(scrollableElem);
  }, []);

  useEffect(() => {
    setScrollThreshold([getScrollIndex(0), getScrollIndex(1), getScrollIndex(2), getScrollIndex(3), getScrollIndex(4), getScrollIndex(5)]);
  }, [scrollableElement?.scrollHeight]); // work around to make it aware of main container height changes.

  const handleSetScrollSpyTabsValue = (index, value) => {
    setScrollSpyTabsValue(prev => {
      let a = [...prev];
      a[index] = value;
      return a;
    });
  };

  useEffect(() => {
    if (tabClicked) {
      return;
    }
    let scrolledIndex = scrollSpyTabsValue.findLastIndex(elem => elem === true);
    setCurrentTab(tabOrder[scrolledIndex !== -1 ? scrolledIndex : 0]);
  }, [scrollSpyTabsValue]);

  const DependencyDrawerTabsDefaultValues = {
    currentTab: currentTab,
    setCurrentTab: setCurrentTab,
    tabOrder: tabOrder,
    scrollSpyTabsValue,
    setScrollSpyTabsValue: handleSetScrollSpyTabsValue,
    setTabClicked: setTabClicked,
    tabClicked: tabClicked
  };

  useEffect(() => {
    if (!scrollableElement) return;

    let scrollTimeout;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) {
        return;
      }

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        setTabClicked(false);
        const scrollTop = scrollableElement.scrollTop;
        const scrollHeight = scrollableElement.scrollHeight;
        const clientHeight = scrollableElement.clientHeight;

        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
        const isAtTop = scrollTop <= 1;

        let updatedSpyValues = scrollThreshold.map(threshold => scrollTop >= threshold);

        if (isAtBottom && !isScrollingProgrammatically.current) {
          updatedSpyValues = scrollThreshold.map((_, idx) => idx === scrollThreshold.length - 1);
        } else if (isAtTop) {
          updatedSpyValues = scrollThreshold.map((_, idx) => idx === 0);
        }

        setScrollSpyTabsValue(updatedSpyValues);
      }, 50);
    };

    scrollableElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [scrollableElement, scrollThreshold]);

  const knownExploitsAlertComponentHeight = 72;

  function getScrollIndex(index) {
    let knownExploitsAlertHeight = !!knownExploitsCount ? knownExploitsAlertComponentHeight : 0;

    let scrollableLength = scrollableElement?.scrollHeight - scrollableElement?.clientHeight;

    let NRSBOMDescriptionContainer = document.querySelector('#NRSBOMDescription');
    //let NRReferencesContainer = document.querySelector('#NRReferences');
    let NRVulnsContainer = document.querySelector('#NRKnownVulns');
    let NRKnownExploitsContainer = document.querySelector('#NRKnownExploits');
    let NRAssociatedFiles = document.querySelector('#NRAssociatedFiles');
    let NRDependencies = document.querySelector('#NRDependencies');
    let prevComponentsHeight = 0;
    switch (index) {
      case 0: //description
        return 0;
      case 1: //vulns
        let prevComponentHeight = knownExploitsAlertHeight + NRSBOMDescriptionContainer?.clientHeight; // relate the prev components height to one third of the scrollable component height
        return prevComponentHeight < scrollableLength / 3 ? prevComponentHeight : scrollableLength / 3;
      case 2: //reference Links
        prevComponentsHeight =
          knownExploitsAlertHeight +
          NRSBOMDescriptionContainer?.clientHeight +
          NRVulnsContainer?.clientHeight +
          NRKnownExploitsContainer?.clientHeight;
        return prevComponentsHeight;
      case 3: //associated files
        prevComponentsHeight =
          knownExploitsAlertHeight +
          NRSBOMDescriptionContainer?.clientHeight +
          NRVulnsContainer?.clientHeight +
          NRKnownExploitsContainer?.clientHeight; /* +
          NRReferencesContainer?.clientHeight;*/
        return prevComponentsHeight;
      case 4:
        prevComponentsHeight =
          knownExploitsAlertHeight +
          NRSBOMDescriptionContainer?.clientHeight +
          NRVulnsContainer?.clientHeight +
          NRKnownExploitsContainer?.clientHeight +
          /*NRReferencesContainer?.clientHeight +*/
          NRAssociatedFiles?.clientHeight;
        return prevComponentsHeight;
      case 5:
        prevComponentsHeight =
          knownExploitsAlertHeight +
          NRSBOMDescriptionContainer?.clientHeight +
          NRVulnsContainer?.clientHeight +
          NRKnownExploitsContainer?.clientHeight +
          /*NRReferencesContainer?.clientHeight +*/
          NRAssociatedFiles?.clientHeight +
          NRDependencies?.clientHeight;
        return prevComponentsHeight;
      default:
        break;
    }
  }

  return (
    <Box>
      <Tabs
        value={currentTab}
        onChange={handleChange}
        textColor="inherit"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="full width tabs example"
      >
        <Tab
          label={<Typography variant={'label'} children={DESCRIPTION} />}
          key={DESCRIPTION}
          value={DESCRIPTION}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={VULNERABILITIES} />}
          key={VULNERABILITIES}
          value={VULNERABILITIES}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        {/*<Tab label={REFERENCE_LINKS} key={REFERENCE_LINKS} value={REFERENCE_LINKS} classes={{root:classes.tab}} />*/}
        <Tab
          label={<Typography variant={'label'} children={ASSOCIATED_FILES} />}
          key={ASSOCIATED_FILES}
          value={ASSOCIATED_FILES}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={DEPENDENCIES} />}
          key={DEPENDENCIES}
          value={DEPENDENCIES}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
        <Tab
          label={<Typography variant={'label'} children={DEPENDENTS} />}
          key={DEPENDENTS}
          value={DEPENDENTS}
          sx={{ borderRight: `solid 1px ${theme.palette.divider}`, borderBottom: `solid 1px ${theme.palette.divider}` }}
        />
      </Tabs>
      <Box
        id={'dependency-details-main-container'}
        sx={{ height: '100%', maxHeight: 'calc(100vh - 176px)', overflowY: 'auto', padding: theme.spacing(3, 3, 6) }}
      >
        <DependencyDrawerTabsContext.Provider value={DependencyDrawerTabsDefaultValues}>
          {!!row && (
            <>
              {!!knownExploitsCount && (
                <Alert severity="error" sx={{ mb: theme.spacing(3), height: theme.spacing(5), alignItems: 'center' }}>
                  <Box component={'span'}>
                    {ALERT.MESSAGE}
                    <Box component={'span'} sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={vulnerabilitiesScrollIntoView}>
                      {ALERT.KNOWN_EXPLOITS}
                    </Box>{' '}
                    present
                    {` (${knownExploitsCount})`}
                  </Box>
                </Alert>
              )}
              <NRSBOMDescription
                dependency={row}
                isLoading={false}
                threshold={scrollThreshold[0]}
                scrollTarget={scrollableElement}
                index={0}
                scrollableId={'NRSBOMDescription'}
              />
              <NRSBOMVulnExploits
                dependency={row}
                isLoading={false}
                threshold={scrollThreshold[1]}
                scrollTarget={scrollableElement}
                index={1}
                scrollableId={'NRKnownVulns'}
              />
              <NRKnownExploits
                cveDetails={knownExploits}
                isLoading={knownExploitsLoading}
                threshold={scrollThreshold[1]}
                scrollTarget={scrollableElement}
                index={1}
                scrollableId={'NRKnownExploits'}
              />
              {/*<NRReferences
                cveDetails={row}
                isLoading={false}
                threshold={scrollThreshold[2]}
                scrollTarget={scrollableElement}
                index={2}
                scrollableId={'NRReferences'}
                title={REFERENCE_LINKS}
                extraMainContainerClasses={classes.references}
                showName={true}
                context={DependencyDrawerTabsContext}
              />*/}
              <NRAssociatedFiles
                isLoading={packageDependenciesByIdLoading}
                threshold={scrollThreshold[3]}
                scrollTarget={scrollableElement}
                index={3}
                scrollableId={'NRAssociatedFiles'}
                title={ASSOCIATED_FILES}
                associatedFiles={associatedFiles || []}
                context={DependencyDrawerTabsContext}
              />
              <NRDependencies
                isLoading={packageDependenciesByIdLoading}
                threshold={scrollThreshold[4]}
                scrollTarget={scrollableElement}
                index={4}
                itemSize={52}
                scrollableId={'NRDependencies'}
                title={DEPENDENCIES}
                dependencies={packageDependenciesByIdCalled && packageDependencies}
                context={DependencyDrawerTabsContext}
                handleNameClick={handleDependencyClick}
              />
              <NRDependencies
                isLoading={packageDependenciesByIdLoading}
                threshold={scrollThreshold[5]}
                scrollTarget={scrollableElement}
                index={5}
                itemSize={29}
                scrollableId={'NRDependents'}
                title={DEPENDENTS}
                dependencies={packageDependenciesByIdCalled && packageDependents}
                context={DependencyDrawerTabsContext}
                showDivider={false}
                handleNameClick={handleDependencyClick}
              />
            </>
          )}
        </DependencyDrawerTabsContext.Provider>
      </Box>
    </Box>
  );
}
export default NRSBOMDrawer;
